/**
 * What a transfer is, in the vocabulary the database uses.
 *
 * A SALE. The one thing in this registry that moves title, and the answer to a
 * sentence `docs/accessFGARules.md` carried for months: "SOLD is still a state
 * this schema cannot express… a transfer needs an `ondo.transfers` table with
 * an offer/accept lifecycle and a grant shape of its own."
 *
 * Must stay in step with the `ondo.transfer_*` and `ondo.vat_scheme` enums in
 * supabase/migrations/20260903090000_transfers.sql. Nothing enforces that but
 * this comment, which is the arrangement every vocabulary here has.
 */
/**
 * WHERE TITLE IS. Deliberately not whether a sale happened — the row is that.
 *
 * The separation is the load-bearing idea in the whole feature. A gallery that
 * has sold a picture has sold it; whether the buyer's name is on the registry
 * is a second question, and it has to be, because a registry that let one party
 * put a work into a stranger's collection would "let anybody reassign anybody's
 * picture by typing".
 *
 * `unregistered` is the honest name for a buyer who is not on ONDO — most art
 * buyers are not, and the design's own Sold Works screen shows names rather than
 * ONDO ids. Calling that `offered` would be pretending a question had been asked
 * of nobody.
 */
export const TRANSFER_STATUSES = [
    "offered",
    "accepted",
    "declined",
    "withdrawn",
    "unregistered",
];
/** How it was sold. A hammer price and a private treaty are different evidence. */
export const TRANSFER_CHANNELS = ["gallery", "auction", "private"];
/**
 * How VAT was accounted for.
 *
 * `margin` is the second-hand goods scheme most of this market runs on, which
 * is why a boolean would not have done.
 */
export const VAT_SCHEMES = ["none", "margin", "standard", "zero"];
export function isTransferStatus(value) {
    return TRANSFER_STATUSES.includes(value);
}
export function isTransferChannel(value) {
    return TRANSFER_CHANNELS.includes(value);
}
/**
 * The three statuses under which a sale STANDS.
 *
 * `declined` is the named buyer saying the record is wrong about them;
 * `withdrawn` is the effecting party saying it is wrong at all. Both leave the
 * row — nothing here is ever deleted — and both mean no sale happened, which is
 * why a work under either is green again. Anything else would make a mis-keyed
 * sale a permanent mark on somebody's picture.
 */
export function saleStands(transfer) {
    return (transfer.status === "offered" ||
        transfer.status === "accepted" ||
        transfer.status === "unregistered");
}
/**
 * Whether title has actually moved on ONDO.
 *
 * A predicate rather than a second column, for the reason the whole schema
 * prefers one. Note that it is FALSE for a completed off-registry sale, and that
 * is not a defect: the work genuinely does still sit in the seller's collection,
 * because there is no principal here to put it with.
 */
export function titleHasMoved(transfer) {
    return transfer.status === "accepted";
}
/**
 * Whether the covenant still runs.
 *
 * `now` is a parameter so the caller supplies the clock. A pure function of its
 * inputs is one a test can put on either side of an expiry without waiting.
 */
export function covenantState(covenant, now = new Date()) {
    if (covenant.unlockedAt !== null)
        return "unlocked";
    return new Date(covenant.endsAt).getTime() > now.getTime() ? "active" : "expired";
}
/** The price and the currency of a sale, as one value. Minor units throughout. */
export function priceOf(transfer) {
    return { minor: transfer.priceMinor, currency: transfer.currency };
}
/**
 * Which act of a recorded sale one history entry is — OND-71.
 *
 * Mirrors `ondo.sale_history_act`; nothing enforces that but this comment and
 * `pnpm test:rls`.
 *
 * `sold` is the line `ondo.effect_sale()` writes, exactly once per transfer by
 * unique index. `corrected` is an approved correction, appended as a new dated
 * row because an entry is never edited. `reversed` is written when the sale does
 * not stand, beside the `sold` row, which is WITHDRAWN rather than deleted so
 * that it and its documents are preserved.
 *
 * NULL on a `sale` entry means somebody wrote it by hand about a change of hands
 * the registry did not watch, which is what the history modal has always
 * produced and goes on producing. `LOAN_HISTORY_ACTS` carries the identical
 * rule, for the identical reason.
 *
 * HOW A READER SEES A SALE'S CURRENT OUTCOME: gather the entries sharing one
 * `saleTransferId` in chain order and read the last act. A withdrawn `sold` row
 * is the unambiguous signal that the sale itself is gone.
 */
export const SALE_HISTORY_ACTS = ["sold", "corrected", "reversed"];
export function isSaleHistoryAct(value) {
    return (typeof value === "string" &&
        SALE_HISTORY_ACTS.includes(value));
}
/**
 * What the selling firm is asking to add to a sale's line after title moved.
 *
 * Mirrors `ondo.sale_addition_kind`. `documents` brings paperwork, which becomes
 * part of the entry on approval; `correction` brings words, which become a new
 * dated entry — the original is left standing, because its documents hang off
 * its id and cannot be re-pointed.
 */
export const SALE_ADDITION_KINDS = ["documents", "correction"];
export function isSaleAdditionKind(value) {
    return (typeof value === "string" &&
        SALE_ADDITION_KINDS.includes(value));
}
/**
 * How a proposed addition finished. Mirrors `ondo.sale_addition_outcome`.
 *
 * `lapsed` is the sale having been reversed under a proposal nobody had
 * answered — the event exists to end the wait, not to tell anybody anything.
 */
export const SALE_ADDITION_OUTCOMES = ["approved", "refused", "lapsed"];
/**
 * One recorded sale party's answer. Mirrors `ondo.sale_addition_stance`.
 *
 * Two values, because "every party must approve the same exact addition" has
 * exactly one alternative — and one refusal settles it.
 */
export const SALE_ADDITION_STANCES = ["approve", "refuse"];
//# sourceMappingURL=transfer.js.map